import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface ContactOption {
  label: string;
  name: string;
  email: string;
}

export type FormEmail = { to: string; name: string; subject: string; body: string };

type FormState = { ready: boolean; sent: boolean; signed: boolean };
export type CppFormStatus = {
  death_benefit: FormState;
  survivor_pension: FormState;
};

// The form keys the backend `forms` allow-list understands.
export type CppFormKeyForm = 'death_benefit' | 'survivor_pension';

interface CppBenefitsSendModalProps {
  open: boolean;
  onClose: () => void;
  sending: boolean;
  contacts: ContactOption[];
  deceasedName: string;
  showSurvivorPension: boolean;
  // Whether the surviving spouse (the ISP-1300 applicant) has an email on file.
  // When false, the survivor section warns the agent to either route the form
  // to another contact or send the Death Benefit only.
  spouseHasEmail: boolean;
  hasExecutor: boolean;
  // Per-form lifecycle status (ready / sent / signed) used to default and gate
  // the include checkboxes: a form can only be sent when it's ready and not
  // already signed; an already-sent form shows as a "Resend".
  formStatus: CppFormStatus;
  defaults: {
    isp1200_executor: { email: string; name: string };
    isp1200_applicant: { email: string; name: string };
    isp1300_spouse: { email: string; name: string };
  };
  onSend: (
    payload: {
      isp1200_executor?: FormEmail;
      isp1200_applicant: FormEmail;
      isp1300_spouse?: FormEmail;
    },
    forms: CppFormKeyForm[],
    options?: { copyLinks?: boolean; copyLinkFor?: 'executor' | 'applicant' | 'spouse' },
  ) => void;
}

interface SectionProps {
  title: string;
  caption?: string;
  contacts: ContactOption[];
  sending: boolean;
  state: FormEmail;
  setState: (next: FormEmail) => void;
}

const Section: React.FC<SectionProps> = ({ title, caption, contacts, sending, state, setState }) => (
  <Stack spacing={1.5}>
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
    {contacts.length > 0 && (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {contacts.map((c) => (
          <Chip
            key={`${c.label}-${c.email}`}
            label={`${c.label}: ${c.name || c.email}`}
            size="small"
            onClick={() => setState({ ...state, to: c.email, name: c.name })}
            disabled={sending || !c.email}
          />
        ))}
      </Stack>
    )}
    <Stack direction="row" spacing={1}>
      <TextField
        label="Recipient name"
        value={state.name}
        onChange={(e) => setState({ ...state, name: e.target.value })}
        disabled={sending}
        size="small"
        sx={{ flex: 1 }}
      />
      <TextField
        label="To (email)"
        value={state.to}
        onChange={(e) => setState({ ...state, to: e.target.value })}
        disabled={sending}
        size="small"
        type="email"
        required
        sx={{ flex: 1.5 }}
      />
    </Stack>
    <TextField
      label="Subject"
      value={state.subject}
      onChange={(e) => setState({ ...state, subject: e.target.value })}
      disabled={sending}
      fullWidth
      size="small"
    />
    <TextField
      label="Message"
      value={state.body}
      onChange={(e) => setState({ ...state, body: e.target.value })}
      disabled={sending}
      fullWidth
      multiline
      minRows={6}
    />
  </Stack>
);

const CppBenefitsSendModal: React.FC<CppBenefitsSendModalProps> = ({
  open,
  onClose,
  sending,
  contacts,
  deceasedName,
  showSurvivorPension,
  spouseHasEmail,
  hasExecutor,
  formStatus,
  defaults,
  onSend,
}) => {
  const execInitial = useMemo<FormEmail>(
    () => ({
      to: defaults.isp1200_executor.email,
      name: defaults.isp1200_executor.name,
      subject: deceasedName
        ? `Action required — CPP Death Benefit for ${deceasedName}`
        : 'Action required — CPP Death Benefit',
      body: `Hello,\n\nAs the executor of ${deceasedName || "the deceased's"} estate, please sign Section D2 of the CPP Death Benefit form to confirm the estate is not applying for the benefit. The person who paid for the funeral will then be able to apply for it.\n\nImportant: please draw your real signature — clearly legible and as close as possible to how you normally sign. Service Canada will reject signatures that look like a scribble or a single line.\n\nThank you,\nThe Cleo Cremation Team`,
    }),
    [defaults.isp1200_executor.email, defaults.isp1200_executor.name, deceasedName],
  );

  const dbInitial = useMemo<FormEmail>(
    () => ({
      to: defaults.isp1200_applicant.email,
      name: defaults.isp1200_applicant.name,
      subject: deceasedName
        ? `Signature required — CPP Death Benefit for ${deceasedName}`
        : 'Signature required — CPP Death Benefit',
      body: `Hello,\n\nHere is the CPP Death Benefit application for ${deceasedName || 'your loved one'}. Click the link in this email to review the form and add your signature.\n\nImportant: please draw your real signature — clearly legible and as close as possible to how you normally sign. Service Canada will reject signatures that look like a scribble or a single line.\n\nOnce you have signed and submitted, we will mail your application to Service Canada on your behalf.\n\nThank you,\nThe Cleo Cremation Team`,
    }),
    [defaults.isp1200_applicant.email, defaults.isp1200_applicant.name, deceasedName],
  );

  const spInitial = useMemo<FormEmail>(
    () => ({
      to: defaults.isp1300_spouse.email,
      name: defaults.isp1300_spouse.name,
      subject: `Signature required — CPP Survivor's Pension`,
      body: `Hello,\n\nHere is your CPP Survivor's Pension application. Click the link in this email to review the form and add your signature.\n\nImportant: please draw your real signature — clearly legible and as close as possible to how you normally sign. Service Canada will reject signatures that look like a scribble or a single line.\n\nOnce you have signed and submitted, we will mail your application to Service Canada on your behalf.\n\nThank you,\nThe Cleo Cremation Team`,
    }),
    [defaults.isp1300_spouse.email, defaults.isp1300_spouse.name],
  );

  const [exec, setExec] = useState<FormEmail>(execInitial);
  const [db, setDb] = useState<FormEmail>(dbInitial);
  const [sp, setSp] = useState<FormEmail>(spInitial);

  // A form can be dispatched only when it's ready and not already signed.
  const canIncludeDb =
    !!formStatus?.death_benefit?.ready && !formStatus?.death_benefit?.signed;
  const canIncludeSp =
    showSurvivorPension &&
    !!formStatus?.survivor_pension?.ready &&
    !formStatus?.survivor_pension?.signed;

  // Which document(s) this send dispatches. Default to every includable form.
  const [includeDb, setIncludeDb] = useState(canIncludeDb);
  const [includeSp, setIncludeSp] = useState(canIncludeSp);

  // Reset whenever the modal opens so re-opening with stale state is safe.
  useEffect(() => {
    if (open) {
      setExec(execInitial);
      setDb(dbInitial);
      setSp(spInitial);
      setIncludeDb(canIncludeDb);
      setIncludeSp(canIncludeSp);
    }
  }, [open, execInitial, dbInitial, spInitial, canIncludeDb, canIncludeSp]);

  const selectedForms = (): CppFormKeyForm[] => [
    ...(includeDb ? (['death_benefit'] as const) : []),
    ...(includeSp ? (['survivor_pension'] as const) : []),
  ];

  const buildPayload = () => ({
    isp1200_applicant: { ...db, to: db.to.trim(), name: db.name.trim(), subject: db.subject.trim() },
    isp1200_executor: hasExecutor
      ? { ...exec, to: exec.to.trim(), name: exec.name.trim(), subject: exec.subject.trim() }
      : undefined,
    isp1300_spouse: includeSp
      ? { ...sp, to: sp.to.trim(), name: sp.name.trim(), subject: sp.subject.trim() }
      : undefined,
  });

  // Send is valid when at least one document is selected and every selected
  // document has the recipient(s) it needs.
  const canSend =
    selectedForms().length > 0 &&
    (!includeDb || (!!db.to.trim() && (!hasExecutor || !!exec.to.trim()))) &&
    (!includeSp || !!sp.to.trim());

  const handleSend = () => {
    if (!canSend) return;
    onSend(buildPayload(), selectedForms());
  };

  // Copy-links flow: generate the DocuSeal submissions but skip emails, then
  // copy the resulting Aura signing URLs to the clipboard. Useful when the
  // agent wants to paste a link into another channel (text, manual email).
  // When `which` is provided, only that signer's link gets copied.
  const handleCopyLinks = (which?: 'executor' | 'applicant' | 'spouse') => {
    if (!canSend) return;
    onSend(buildPayload(), selectedForms(), { copyLinks: true, copyLinkFor: which });
  };

  const [copyMenuAnchor, setCopyMenuAnchor] = useState<null | HTMLElement>(null);
  const copyMenuOpen = Boolean(copyMenuAnchor);
  // Build the list of signers that have a copyable link for the selected forms.
  const copyOptions: Array<{ key: 'executor' | 'applicant' | 'spouse'; label: string }> = [];
  if (includeDb && hasExecutor) copyOptions.push({ key: 'executor', label: 'Executor' });
  if (includeDb) copyOptions.push({ key: 'applicant', label: 'Applicant (paying client)' });
  if (includeSp && sp.to.trim()) copyOptions.push({ key: 'spouse', label: 'Spouse' });

  return (
    <>
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send CPP Benefits for Signature</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography variant="caption" color="text.secondary">
            Tick the document(s) to send. Each signer is emailed independently.
            The Death Benefit applicant is the paying client named in Section C3,
            and they sign Section E. When a third-party executor is named, they
            receive their email first; the applicant's email is sent
            automatically only after the executor finishes signing.
          </Typography>

          {/* ── CPP Death Benefit ───────────────────────────────────── */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeDb}
                  onChange={(e) => setIncludeDb(e.target.checked)}
                  disabled={sending || !canIncludeDb}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  CPP Death Benefit
                  {formStatus?.death_benefit?.signed
                    ? ' — already signed (discard to resend)'
                    : !formStatus?.death_benefit?.ready
                      ? ' — not marked Ready yet'
                      : formStatus?.death_benefit?.sent
                        ? ' — Resend (previous link will stop working)'
                        : ''}
                </Typography>
              }
            />
          </Box>
          {includeDb && (
            <>
              {hasExecutor && (
                <>
                  <Section
                    title="Death Benefit — Executor (signs first)"
                    caption="Section D2 — declines the benefit on behalf of the estate."
                    contacts={contacts}
                    sending={sending}
                    state={exec}
                    setState={setExec}
                  />
                  <Divider />
                </>
              )}
              <Section
                title={
                  hasExecutor
                    ? 'Death Benefit — Applicant, the paying client (signs after executor)'
                    : 'Death Benefit — Applicant (paying client)'
                }
                caption={
                  hasExecutor
                    ? 'Section E. Sent automatically when the executor finishes.'
                    : 'Section E — signed by the paying client named in Section C3.'
                }
                contacts={contacts}
                sending={sending}
                state={db}
                setState={setDb}
              />
            </>
          )}

          {/* ── CPP Survivor's Pension ──────────────────────────────── */}
          {showSurvivorPension && (
            <>
              <Divider />
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeSp}
                      onChange={(e) => setIncludeSp(e.target.checked)}
                      disabled={sending || !canIncludeSp}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      CPP Survivor's Pension
                      {formStatus?.survivor_pension?.signed
                        ? ' — already signed (discard to resend)'
                        : !formStatus?.survivor_pension?.ready
                          ? ' — not marked Ready yet'
                          : formStatus?.survivor_pension?.sent
                            ? ' — Resend (previous link will stop working)'
                            : ''}
                    </Typography>
                  }
                />
              </Box>
              {includeSp && !spouseHasEmail && !sp.to.trim() && (
                <Alert severity="warning">
                  The surviving spouse has <strong>no email on file</strong>. Pick
                  another contact below (e.g. the paying or authorized client) to
                  receive it and help them sign, or untick this document.
                </Alert>
              )}
              {includeSp && !spouseHasEmail && !!sp.to.trim() && (
                <Alert severity="info">
                  The surviving spouse has no email on file — this Survivor's
                  Pension will go to <strong>{sp.to.trim()}</strong> to help them
                  sign on their behalf.
                </Alert>
              )}
              {includeSp && (
                <Section
                  title="Survivor's Pension"
                  caption="Sent to the surviving spouse (or a contact helping them)."
                  contacts={contacts}
                  sending={sending}
                  state={sp}
                  setState={setSp}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={sending || !canSend}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          Send
        </Button>
        {copyOptions.length === 1 ? (
          <Button
            onClick={() => handleCopyLinks(copyOptions[0].key)}
            variant="outlined"
            color="primary"
            disabled={sending || !canSend}
            startIcon={<LinkIcon />}
          >
            Copy link
          </Button>
        ) : (
          <>
            <Button
              onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
              variant="outlined"
              color="primary"
              disabled={sending || !canSend}
              startIcon={<LinkIcon />}
              endIcon={<ArrowDropDownIcon />}
            >
              Copy link
            </Button>
            <Menu
              anchorEl={copyMenuAnchor}
              open={copyMenuOpen}
              onClose={() => setCopyMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setCopyMenuAnchor(null);
                  handleCopyLinks();
                }}
              >
                All signers
              </MenuItem>
              <Divider />
              {copyOptions.map((opt) => (
                <MenuItem
                  key={opt.key}
                  onClick={() => {
                    setCopyMenuAnchor(null);
                    handleCopyLinks(opt.key);
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CppBenefitsSendModal;
